import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  AffidavitMarks,
  AffidavitStamps,
  DEFAULT_LAYOUT_ITEM,
  StampLayoutItem,
  StampTextPosition,
  STAMP_INK,
} from "./affidavitStamps";

/**
 * The affidavit page, as the Affidavit Certificate dialog previews it and as
 * POST /affidavit-certificate/add renders it to PDF — also printed inside the
 * PAN form PDF of an AFFIDAVIT application.
 *
 * Laid out like a notarised affidavit: ₹10 ticket top left, two ₹5 tickets top
 * right, the notary seal over each group, "NOTED & REGISTERED" with the date
 * and serial number on the right of the title, and "ATTESTED" at the bottom
 * centre. Anything not uploaded (or no serial) is simply left out.
 */

export type AffidavitDetails = {
  applicantName: string;
  fatherName: string;
  dob: string;
  address: string;
  place: string;
};

type Props = AffidavitDetails & {
  /**
   * The footer DATE, e.g. "19 October 2026". Used only when there is no
   * serial; otherwise the noted seal's date (marks.footerDate) is printed.
   */
  todayDate: string;
  /** Applicant's signature (data-URL); "" leaves the slot empty. */
  signature?: string;
  acknowledgementNumber?: string | number | null;
  stamps?: AffidavitStamps | null;
  marks?: AffidavitMarks | null;
  /**
   * "pdf": the copy posted to /affidavit-certificate/add, printed by
   * puppeteer's page.pdf(). Puppeteer does not load the admin's CSS, so there
   * #pdfContent is taller than A4 and an `absolute` ack tag would fall onto a
   * second page; `fixed` pins it to the printed page's corner.
   *
   * "image": a page inside the PAN form PDF, which is a screenshot of
   * #pdfContent, scaled to the A4 width. The box is the same as in "pdf"
   * (240 × 327 mm without the admin's CSS, which page.pdf() shrinks to fit A4),
   * so both copies look alike; the ack tag and the attested seal stay
   * `absolute` to the box, whose foot is the foot of the page there.
   */
  printMode?: "pdf" | "image";
};

// Page colour of the affidavit attached inside the PAN form ("image" mode):
// plain white, printed black & white (client 22 Oct: no green page colour).
export const AFFIDAVIT_ATTACHED_BG = "#fff";

// Last 6 digits of the acknowledgement number, printed bottom-right.
export const ackLastSix = (ack?: string | number | null) =>
  String(ack ?? "").replace(/\D/g, "").slice(-6);

const labelStyle: React.CSSProperties = {
  display: "inline-block",
  width: "220px",
  fontWeight: 500,
};

const hrStyle: React.CSSProperties = {
  border: "none",
  borderTop: "1px solid #555",
  margin: "15px 0 8px",
};

// The fill-in rule the footer's Place / Date values print on.
const dottedValueStyle: React.CSSProperties = {
  flex: 1,
  marginLeft: "12px",
  paddingBottom: "3px",
  borderBottom: "1px dotted #555",
  minHeight: "20px",
};

/**
 * Sizes and places measured off a real notarised affidavit (A4 scan):
 * tickets about 30 mm tall, the round notary seal about 43 mm across.
 */
const TICKET_HEIGHT_MM = 31;
const TICKET_MAX_WIDTH_MM = 26;
const NOTARY_SIZE_MM = 43;

/** `mm` scaled by a layout item's size (100 = default). */
const scaled = (mm: number, item: StampLayoutItem) => `${(mm * item.size) / 100}mm`;

const Ticket = ({ src, item }: { src: string; item: StampLayoutItem }) => (
  <img
    src={src}
    alt="Stamp ticket"
    style={{
      height: scaled(TICKET_HEIGHT_MM, item),
      width: "auto",
      maxWidth: scaled(TICKET_MAX_WIDTH_MM, item),
      display: "block",
    }}
  />
);

/** Centred over its ticket group, then moved / sized by its layout item. */
const NotarySeal = ({
  src,
  rotate,
  item,
}: {
  src: string;
  rotate: number;
  item: StampLayoutItem;
}) => (
  <img
    src={src}
    alt="Notary seal"
    style={{
      position: "absolute",
      top: `calc(50% + ${item.y}mm)`,
      left: `calc(50% + ${item.x}mm)`,
      width: scaled(NOTARY_SIZE_MM, item),
      height: scaled(NOTARY_SIZE_MM, item),
      objectFit: "contain",
      transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
      opacity: 0.9,
    }}
  />
);

/** Text image placed at a % position on a seal image (StampTextPosition). */
const SealText = ({ src, pos }: { src: string; pos: StampTextPosition }) =>
  src ? (
    <img
      src={src}
      alt=""
      style={{
        position: "absolute",
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        // fontSize is the text height in px; the image carries some air around it.
        height: `${Math.round(pos.fontSize * 1.5)}px`,
        width: "auto",
        transform: "translateY(-50%)",
      }}
    />
  ) : null;

/** "NOTED & REGISTERED" drawn in stamp ink when no seal image is uploaded. */
const NotedTextSeal = ({ marks }: { marks: AffidavitMarks }) => (
  <div
    style={{
      color: STAMP_INK,
      fontFamily: "Arial, sans-serif",
      lineHeight: 1.15,
      transform: "rotate(-1.5deg)",
    }}
  >
    <div style={{ fontWeight: 900, fontSize: "17px", letterSpacing: "0.5px" }}>
      NOTED &amp; REGISTERED
    </div>
    <div style={{ display: "flex", alignItems: "flex-end", fontWeight: 700, fontSize: "15px" }}>
      <span>Date</span>
      <span style={{ flex: 1, borderBottom: `1.5px dotted ${STAMP_INK}`, marginLeft: "4px" }}>
        <img src={marks.notedDate} alt="" style={{ height: "30px", display: "block" }} />
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "flex-end", fontWeight: 700, fontSize: "15px" }}>
      <span style={{ whiteSpace: "nowrap" }}>At Sr. No.</span>
      <span style={{ flex: 1, borderBottom: `1.5px dotted ${STAMP_INK}`, marginLeft: "4px" }}>
        <img src={marks.serial} alt="" style={{ height: "30px", display: "block" }} />
      </span>
    </div>
  </div>
);

const AffidavitTemplate = ({
  applicantName,
  fatherName,
  dob,
  address,
  place,
  todayDate,
  signature,
  acknowledgementNumber,
  stamps,
  marks,
  printMode,
}: Props) => {
  const ack = ackLastSix(acknowledgementNumber);
  const hasNoted = !!marks?.serial;
  const hasAttested = !!stamps?.attestedSeal;
  // Size / place of each ticket and seal, set on Affidavit Stamp & Seal.
  const layout = stamps?.layout;
  const at = (key: keyof NonNullable<typeof layout>) => layout?.[key] || DEFAULT_LAYOUT_ITEM;
  const ticket10 = at("ticket10");
  const ticket5 = at("ticket5");
  const noted = at("noted");
  const attested = at("attested");

  const attached = printMode === "image";

  const page = (
    <div
      id={attached ? undefined : "pdfContent"}
      style={{
        width: "210mm",
        minHeight: "297mm",
        margin: attached ? 0 : "auto",
        background: attached ? "transparent" : "#fff",
        // Inside the PAN form the affidavit prints black & white, like the
        // other attached documents, on a white page (outer box).
        filter: attached ? "grayscale(100%)" : undefined,
        padding: "15mm 15mm 15mm",
        fontFamily: "Arial, sans-serif",
        fontSize: "17px",
        color: "#000",
        lineHeight: "1.6",
        position: "relative",
      }}
    >
      {/* TOP BAND — the stamp tickets, with the notary seal over each group. */}
      <div style={{ height: "80mm", position: "relative" }}>
        {stamps?.ticket10 && (
          <div
            style={{
              position: "absolute",
              left: `${12 + ticket10.x}mm`,
              top: `${30 + ticket10.y}mm`,
            }}
          >
            <Ticket src={stamps.ticket10} item={ticket10} />
            {stamps.notarySeal && (
              <NotarySeal src={stamps.notarySeal} rotate={-8} item={at("notaryLeft")} />
            )}
          </div>
        )}
        {stamps?.ticket5 && (
          <div
            style={{
              position: "absolute",
              right: `${7 - ticket5.x}mm`,
              top: `${30 + ticket5.y}mm`,
              display: "flex",
              gap: "1mm",
            }}
          >
            <Ticket src={stamps.ticket5} item={ticket5} />
            <Ticket src={stamps.ticket5} item={ticket5} />
            {stamps.notarySeal && (
              <NotarySeal src={stamps.notarySeal} rotate={6} item={at("notaryRight")} />
            )}
          </div>
        )}
      </div>

      {/* NOTED & REGISTERED — right of the title block. */}
      {hasNoted && marks && (
        <div
          style={{
            position: "absolute",
            right: `${3 - noted.x}mm`,
            top: `${93 + noted.y}mm`,
            width: "56mm",
            // Scaled as a whole, so the date / Sr. No. written on it stay in place.
            transform: `scale(${noted.size / 100})`,
            transformOrigin: "top right",
          }}
        >
          {stamps?.notedSeal ? (
            <div style={{ position: "relative" }}>
              <img
                src={stamps.notedSeal}
                alt="Noted seal"
                style={{ width: "100%", display: "block" }}
              />
              <SealText src={marks.notedDate} pos={stamps.notedDatePos} />
              <SealText src={marks.serial} pos={stamps.notedSerialPos} />
            </div>
          ) : (
            <NotedTextSeal marks={marks} />
          )}
        </div>
      )}

      {/* TITLE — kept clear of the noted seal when there is one. */}
      <div style={{ textAlign: "center", marginRight: hasNoted ? "50mm" : 0 }}>
        <h2
          style={{
            margin: 0,
            fontSize: "23px",
            fontWeight: 700,
          }}
        >
          AFFIDAVIT
        </h2>

        <div style={{ fontSize: "17px" }}>(For the Purpose of PAN Card only)</div>

        <div style={{ fontSize: hasNoted ? "16px" : "17px", marginTop: "5px", whiteSpace: "nowrap" }}>
          Affidavit sworn before a magistrate stating the date of birth
        </div>
      </div>

      {/* DETAILS */}
      <div
        style={{
          marginTop: "30px",
          width: "70%",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <span style={{ ...labelStyle, flexShrink: 0 }}>NAME OF APPLICANT</span>
          <span>:</span>
          <span style={{ marginLeft: "20px" }}>{applicantName}</span>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <span style={{ ...labelStyle, flexShrink: 0 }}>FATHER'S NAME</span>
          <span>:</span>
          <span style={{ marginLeft: "20px" }}>{fatherName}</span>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <span style={{ ...labelStyle, flexShrink: 0 }}>DATE OF BIRTH</span>
          <span>:</span>
          <span style={{ marginLeft: "20px" }}>{dob || "DD/MM/YYYY"}</span>
        </div>

        <div style={{ marginTop: "25px", display: "flex", alignItems: "flex-start" }}>
          <span style={{ ...labelStyle, flexShrink: 0 }}>ADDRESS</span>
          <span>:</span>
          <span style={{ marginLeft: "20px" }}>{address?.toUpperCase()}</span>
        </div>
      </div>

      {/* LINE */}
      <div style={hrStyle}></div>

      {/* HEADING */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "15px",
        }}
      >
        <span
          style={{
            borderBottom: "1px solid #555",
            padding: "0 35px 5px",
          }}
        >
          ON SOLEMN AFFIRMATION I AM TO STATE AS UNDER
        </span>
      </div>

      {/* BODY */}
      <div
        style={{
          marginTop: "15px",
          textAlign: "justify",
          fontSize: "17px",
        }}
      >
        THAT I AM {applicantName?.toUpperCase()} S/D/O {fatherName?.toUpperCase()}{" "}
        RESIDENT OF - {address?.toUpperCase()}
      </div>

      {/* VERIFICATION */}
      <div
        style={{
          textAlign: "center",
          marginTop: "25px",
          marginBottom: "15px",
          textDecoration: "underline",
          fontSize: "17px",
        }}
      >
        VERIFICATION
      </div>

      <div
        style={{
          textAlign: "justify",
          padding: "0 15px",
          fontSize: "17px",
        }}
      >
        1. {applicantName?.toUpperCase()} S/D/O {fatherName?.toUpperCase()} HERE BY
        CONFIRM THAT THE CONTENT OF ABOVE AFFIDAVIT CONTENT THEREIN GIVEN
        INFORMATION IS TRUE AND CORRECT TO THE BEST OF MY PERSONAL KNOWLEDGE AND
        BELIEF.
      </div>

      {/* FOOTER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "60px",
          padding: "0 15px",
        }}
      >
        {/* Place and Date each sit on their own dotted rule, so the notary
            can strike through and re-write either one by hand. */}
        <div style={{ width: "300px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              marginBottom: "30px",
            }}
          >
            <span style={{ width: "70px", flexShrink: 0 }}>PLACE</span>
            <span style={{ flexShrink: 0 }}>:</span>
            <span style={{ ...dottedValueStyle }}>{place?.toUpperCase() || "N/A"}</span>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <span style={{ width: "70px", flexShrink: 0 }}>DATE</span>
            <span style={{ flexShrink: 0 }}>:</span>
            {/* With a serial, the same day as the noted seal (the day before). */}
            <span style={{ ...dottedValueStyle }}>{marks?.footerDate || todayDate}</span>
          </div>
        </div>

        <div
          style={{
            width: "300px",
            textAlign: "center",
          }}
        >
          {/* Fixed-height slot, so the line sits at the same height with or
              without a signature in it. */}
          <div
            style={{
              height: "70px",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            {signature && (
              <img
                src={signature}
                alt="Signature"
                style={{
                  maxWidth: "220px",
                  maxHeight: "66px",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
          <div
            style={{
              borderTop: "1px solid #555",
              paddingTop: "10px",
            }}
          >
            SIGNATURE / THUMB IMPRESSION
          </div>
        </div>
      </div>

      {/*
        ATTESTED — bottom centre of the page, with its date written on it.
        Pinned to the page like the ack tag (not in the text flow), so a long
        address never pushes it onto a second page; it may overlap the foot of
        the text, as a hand stamp does.
      */}
      {hasAttested && stamps && (
        <div
          style={{
            position: printMode === "pdf" ? "fixed" : "absolute",
            left: `calc(50% + ${attested.x}mm)`,
            bottom: `${12 - attested.y}mm`,
            transform: `translateX(-50%) scale(${attested.size / 100})`,
            transformOrigin: "bottom center",
            height: "34mm",
          }}
        >
          <img
            src={stamps.attestedSeal}
            alt="Attested seal"
            style={{ height: "34mm", width: "auto", display: "block", transform: "rotate(-4deg)" }}
          />
          {marks?.attestedDate && (
            <SealText src={marks.attestedDate} pos={stamps.attestedDatePos} />
          )}
        </div>
      )}

      {/* Last 6 digits of the ack number, bottom-right of the page. */}
      {ack && (
        <div
          data-ack-last6
          style={{
            position: printMode === "pdf" ? "fixed" : "absolute",
            right: "15mm",
            bottom: "10mm",
            fontSize: "14px",
          }}
        >
          {ack}
        </div>
      )}
    </div>
  );

  if (!attached) return page;
  return (
    <div
      id="pdfContent"
      // Shrink-wraps the page box (wider than 210mm without the admin CSS).
      style={{ width: "fit-content", margin: "auto", background: AFFIDAVIT_ATTACHED_BG }}
    >
      {page}
    </div>
  );
};

/** The page as HTML: "pdf" for POST /affidavit-certificate/add, "image" for a PAN form extra page. */
export const affidavitHtml = (
  props: Omit<Props, "printMode">,
  printMode: "pdf" | "image" = "pdf"
) => renderToStaticMarkup(<AffidavitTemplate {...props} printMode={printMode} />);

export default AffidavitTemplate;
